import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useToast } from "../../../hooks/use-toast";
import {
    ChevronLeft,
    ChevronRight,
    FileTerminal,
    Folder,
    Globe,
    Laptop,
    Monitor,
    Search,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Card } from "../../ui/card";
import { Skeleton } from "../interfaces/CommonInterfaces";

import { myLocalStorage } from "../../StorageHelper";
import { fetchCategories, getCategoryItems, updateCategory } from "./Api/policyprofileapi";
import { AppResource } from "./interface";
import { fetchUrlControlDataWithProfile, getFoldersForProfile } from "../Resources/Api/resourceApi";
import { getProfileResourceNameField } from "./helper";
import useUserStore from "../../../services/userStore";
import { getAllCategoryItemsWithoutProfileId, getCategoryItemsWithoutProfileId } from "../ResourceCategory/Api/resourcecategoryapi";

export default function PolicyProfilesCategories() {
    const location = useLocation();
    const { id, osType: osTypeParam } = useParams();
    const [selectedResourceType, setSelectedResourceType] = useState<"app" | "folder" | "url">("app");
    const [selectedCategory, setSelectedCategory] = useState<AppResource | null>(null);
    const [categoriesSearch, setCategoriesSearch] = useState("");
    const [itemsSearch, setItemsSearch] = useState("");
    const [currentCategoriesPage, setCurrentCategoriesPage] = useState(1);
    const [currentItemsPage, setCurrentItemsPage] = useState(1);
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const [data, setData] = useState<AppResource[]>([]);
    const profileId = id ? Number(id) : -1;
    const resourceNameField = getProfileResourceNameField(selectedResourceType);
    const [matchedResources, setMatchedResources] = useState<any[]>([]);
    const [categoryTrustLevels, setCategoryTrustLevels] = useState<Record<string, string>>({});
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [itemsLoading, setItemsLoading] = useState(false);
    const { role, userId, email } = useUserStore((state) => state.user);

    const trustLevelOptions = [
        { value: "not_configured", label: "NOT CONFIGURED" },
        { value: "ENABLED", label: "ALLOWED" },
        { value: "DISABLED", label: "DENIED" },
        { value: "DYNAMIC", label: "TIMED" }
    ];

    const CATEGORIES_PER_PAGE = 6;
    const ITEMS_PER_PAGE = 10;

    const profileName = location.state?.profileName || localStorage.getItem("currentProfileName") || "Unknown Profile";
    const osType = (osTypeParam as "windows" | "linux" | "mac") || "windows";

    const getCategoryTrustKey = (categoryName: string, resourceType?: string) =>
        `${resourceType}:${categoryName}`;

    const handleTrustLevelChange = async (categoryName: string, profileName: string, trustLevel: string, category: AppResource) => {
        const trustKey = getCategoryTrustKey(categoryName, category.resourceType);
        const previousTrustLevel = getCategoryTrustLevel(category);
        const profileExists = category.profiles.some(p => p.profileId === profileId);
        const updatedProfiles = profileExists
            ? category.profiles.map(p => ({
                profileId: p.profileId,
                name: p.name,
                trustLevel: p.profileId === profileId ? trustLevel : p.trustLevel,
            }))
            : [
                ...category.profiles,
                {
                    profileId,
                    name: profileName,
                    trustLevel,
                },
            ];

        setCategoryTrustLevels(prev => ({
            ...prev,
            [trustKey]: trustLevel,
        }));

        let osType = (category.operationSystem.toUpperCase() === "MAC" || category.operationSystem.toUpperCase() === "MACOS") ? "MACOS" : category.operationSystem.toUpperCase();


        const payload = {
            reputationScore: category.reputationScore,
            oldName: category.name,
            newName: category.name,
            oldSearchPatterns: category.searchPatterns,
            os: osType,
            resourceType: category.resourceType,
            profiles: updatedProfiles,
            userEmail: email,
            tenantName: category.tenantName,
        };

        toast({
            title: "Trust level updating",
            description: `Trust level for "${categoryName}" → "${profileName}" updating to ${trustLevelOptions.find(opt => opt.value === trustLevel)?.label ?? trustLevel
                }`,
        });

        try {
            const response = await updateCategory(payload);
            if (response) {
                fetchData(false);
            }
            toast({
                title: "Trust level updated",
                description: `Trust level for "${categoryName}" → "${profileName}" set to ${trustLevelOptions.find(opt => opt.value === trustLevel)?.label ?? trustLevel
                    }`,
            });
        } catch (error: any) {
            setCategoryTrustLevels(prev => ({
                ...prev,
                [trustKey]: previousTrustLevel,
            }));
            toast({
                title: "Trust level update failed",
                description: typeof error === "string"
                    ? error
                    : error?.message || `Unable to update trust level for "${categoryName}".`,
                variant: "destructive",
            });
        }

    };

    const getTrustLevelCardStyling = (trustLevel?: string) => {
        const base =
            "bg-card text-card-foreground shadow-sm p-4 rounded-lg cursor-pointer transition-all duration-300 border border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-offset-2 ring-offset-background";

        switch (trustLevel) {
            case "ENABLED":
                return `${base} border-l-8 border-l-green-500 dark:border-l-green-400 focus-visible:ring-green-500 hover:ring-2 hover:ring-green-400`;
            case "DISABLED":
                return `${base} border-l-8 border-l-orange-500 dark:border-l-orange-400 focus-visible:ring-orange-500 hover:ring-2 hover:ring-orange-400`;
            case "DYNAMIC":
                return `${base} border-l-8 border-l-blue-500 dark:border-l-blue-400 focus-visible:ring-blue-500 hover:ring-2 hover:ring-blue-400`;
            case "not_configured":
            default:
                return `${base} border-l-8 border-l-gray-400 dark:border-l-gray-500 focus-visible:ring-gray-500 hover:ring-2 hover:ring-gray-400`;
        }
    };


    const getResourceTypeIcon = (resourceType: string) => {
        switch (resourceType) {
            case "app": return <FileTerminal className="w-4 h-4" />;
            case "folder": return <Folder className="w-4 h-4" />;
            case "url": return <Globe className="w-4 h-4" />;
            default: return <FileTerminal className="w-4 h-4" />;
        }
    };

    const getOSIcon = (os: string) => {
        switch (os) {
            case "windows": return <Monitor className="w-4 h-4" />;
            case "linux": return <Laptop className="w-4 h-4" />;
            case "mac": return <Monitor className="w-4 h-4" />;
            default: return <Monitor className="w-4 h-4" />;
        }
    };

    const getResourceTypeName = (resourceType: string) => {
        switch (resourceType) {
            case "app": return "Applications";
            case "folder": return "Folders";
            case "url": return "URLs";
            default: return "Resources";
        }
    };

    const getCategoryTrustLevel = (category: AppResource | null) => {
        if (!category) return "not_configured";
        return categoryTrustLevels[getCategoryTrustKey(category.name, category.resourceType)] ??
            category.profiles?.find(p => p.name === profileName)?.trustLevel ?? "not_configured";
    };

    const isNotConfiguredTrustLevel = (trustLevel?: string) => {
        return trustLevel === "not_configured";
    };

    const fetchData = async (isFirst = true) => {
        setCategoriesLoading(isFirst);
        const response: AppResource[] = await fetchCategories(osType, tenantName, selectedResourceType);



        setData(response.filter(cat => cat.operationSystem.toLowerCase() === (osType === "mac" ? "macos" : osType).toLowerCase()));
        setCategoriesLoading(false);
    };

    const fetchCategoryItems = async (category: AppResource | null) => {
        if (!category) return;

        const trustLevel = getCategoryTrustLevel(category);


        setItemsLoading(true);

        let response = [];
        if (selectedResourceType === "app" && isNotConfiguredTrustLevel(trustLevel)) {
            response = await getCategoryItemsWithoutProfileId(
                osType,
                tenantName,
                selectedResourceType,
                category.name
            );
        } else {
            response = await getCategoryItems(
                osType,
                tenantName,
                selectedResourceType,
                category.name,
                profileId
            );
        }



        const manualExclusions = Array.isArray(category.manualExclusions) ? category.manualExclusions : [];
        const exclusionNames = manualExclusions.map(e => e.name);

        let filteredResponse: any[] = [];

        if (selectedResourceType === "app") {
            const normalizedResponse = Array.isArray(response)
                ? response.map((entry: any) => {
                    if (entry?.defaultProfileAppsResource) return entry;

                    return {
                        defaultProfileAppsResource: {
                            profileId,
                            appId: entry?.id,
                            profileName: decodeURIComponent(profileName),
                            appName: entry?.name,
                            appPath: entry?.path,
                            size: entry?.size,
                            fingerprintSha256: entry?.fingerprintSha256,
                            signature: entry?.signature,
                            type: entry?.type || "ORIGINAL",
                            profileResource: "INHERITED",
                            trustLevel: trustLevel === "not_configured" ? "DISABLED" : trustLevel,
                            categoryName: entry?.categories?.[0]?.categoryName || category.name,
                            reputationScore: entry?.reputationScore,
                        },
                        applicationReputation: null,
                    };
                })
                : [];

            const seen = new Set<string>();
            filteredResponse = normalizedResponse.filter((entry: any) => {
                const appPath = entry?.defaultProfileAppsResource?.appPath;
                if (!appPath || exclusionNames.includes(appPath) || seen.has(appPath)) return false;
                seen.add(appPath);
                return true;
            });
        } else {
            let profileResourceList: any[] = [];
            if (selectedResourceType === "folder") {
                const folderResponse = await getFoldersForProfile(profileId, osType);
                profileResourceList = folderResponse.sort((a: any, b: any) => a.folderId - b.folderId);
            } else {
                profileResourceList = await fetchUrlControlDataWithProfile(profileId, osType);
            }

            const filteredResources = response.content.filter((resource: any) =>
                resource.categories?.some((c: any) => c.categoryName.toLowerCase() === category.name.toLowerCase())
            );

            const seen = new Set<string>();
            filteredResponse = profileResourceList.filter((profileResource: any) => {
                const match = filteredResources.some((categoryResource: any) =>
                    profileResource[resourceNameField] === categoryResource.path ||
                    profileResource[resourceNameField] === categoryResource.name
                );
                const key = profileResource[resourceNameField];
                if (!match || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        setMatchedResources(filteredResponse);
        setItemsLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [osType, tenantName, selectedResourceType]);

    useEffect(() => {
        fetchCategoryItems(selectedCategory);
    }, [selectedCategory, categoryTrustLevels, selectedResourceType]);

    const filteredCategories = useMemo(() => {
        return data.filter(cat => cat.name.toLowerCase().includes(categoriesSearch.toLowerCase()));
    }, [categoriesSearch, data]);

    const filteredItems = useMemo(() => {
        if (!selectedCategory) return [];

        return matchedResources.filter((item) => {

            let displayValue = "";
            switch (selectedResourceType) {
                case "app":
                    displayValue = item?.defaultProfileAppsResource?.appPath ?? "";
                    break;
                case "folder":
                    displayValue = item?.path ?? "";
                    break;
                case "url":
                    displayValue = item?.urlName ?? "";
                    break;
                default:
                    displayValue = "";
            }

            const search = itemsSearch.toLowerCase();
            if (search.trim().length > 0) {
                return displayValue?.toLowerCase().includes(search)
            } else return item

        });
    }, [matchedResources, selectedCategory, itemsSearch, selectedResourceType]);


    const totalCategoriesPages = Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE);
    const paginatedCategories = useMemo(() => {
        const start = (currentCategoriesPage - 1) * CATEGORIES_PER_PAGE;
        return filteredCategories.slice(start, start + CATEGORIES_PER_PAGE);
    }, [filteredCategories, currentCategoriesPage]);

    const totalItemsPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    const paginatedItems = useMemo(() => {
        const start = (currentItemsPage - 1) * ITEMS_PER_PAGE;
        return filteredItems.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredItems, currentItemsPage]);

    // Skeleton card structure
    const SkeletonCard = () => (
        <Card className="p-4 mb-2 animate-pulse">
            <div className="flex items-center gap-3">
                <Skeleton className="w-4 h-4 rounded" />
                <div className="flex-1 space-y-2">
                    <Skeleton className="w-3/4 h-4 rounded" />
                    <Skeleton className="w-1/2 h-3 rounded" />
                </div>
            </div>
        </Card>
    );

    return (
        <div className="flex-1 overflow-auto bg-background">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-background flex items-center space-x-2 text-sm text-muted-foreground">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.history.back()}
                    className="p-0 h-auto hover:bg-transparent"
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <span>Policy Profiles / {decodeURIComponent(profileName)} / Categories</span>
            </div>

            <div className="px-6 py-3 border-b bg-background flex items-center justify-between">
                <h1 className="text-xl font-bold text-foreground">{decodeURIComponent(profileName)} - Categories</h1>
                <div className="flex items-center gap-2">
                    {getOSIcon(osType)}
                    <span className="text-sm text-muted-foreground capitalize">• {osType} Policy Profile</span>
                </div>
            </div>

            {/* Tabs */}
            <div className="h-full flex flex-col bg-white dark:bg-background">
                <Tabs
                    value={selectedResourceType}
                    onValueChange={(value) => {
                        setSelectedResourceType(value as "app" | "folder" | "url");
                        setSelectedCategory(null);
                        setMatchedResources([]);
                        setItemsSearch("");
                        setCurrentItemsPage(1);
                        setCategoriesSearch("");
                        setCurrentCategoriesPage(1);
                    }}
                    className="flex-1 flex flex-col"
                >
                    <div className="flex justify-center">
                        <TabsList className="grid grid-cols-3 bg-muted/50 p-1 h-11 w-auto justify-center">
                            {["app", "folder", "url"].map(type => (
                                <TabsTrigger
                                    key={type}
                                    value={type}
                                    className="gap-2 font-medium data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-300"
                                >
                                    {getResourceTypeIcon(type)} {getResourceTypeName(type).toUpperCase()}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>
                    <TabsContent value={selectedResourceType} className="flex-1 flex gap-6 p-6 m-0 transition-all duration-300">
                        {/* Categories */}
                        <div className="flex-1">
                            <Input
                                placeholder={`Search ${selectedResourceType} categories...`}
                                value={categoriesSearch}
                                onChange={e => setCategoriesSearch(e.target.value)}
                                className="pl-10 relative mb-4"
                            />
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />

                            <div className="space-y-2 mb-4">
                                {categoriesLoading ? (
                                    Array(CATEGORIES_PER_PAGE).fill(0).map((_, idx) => <SkeletonCard key={idx} />)
                                ) : (
                                    paginatedCategories
                                        .filter((category) => {
                                            // Apply same OS filter logic
                                            const os = category.operationSystem?.toUpperCase();
                                            const profileOS = osType?.toUpperCase();

                                            return (
                                                os === profileOS ||
                                                (os === "MACOS" && profileOS === "MAC")
                                            );
                                        })
                                        .map((category, idx) => {
                                            const trustLevel = getCategoryTrustLevel(category);
                                            return (
                                                <Card
                                                    key={idx}
                                                    className={`p-4 rounded-lg cursor-pointer transition-all duration-300 ${getTrustLevelCardStyling(trustLevel)} ${selectedCategory?.name === category.name ? 'ring-2 ring-blue-500' : ''}`}
                                                    onClick={() => {
                                                        setSelectedCategory(category);
                                                        setCurrentItemsPage(1);
                                                        setItemsSearch("");
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3 flex-1">
                                                            {getResourceTypeIcon(selectedResourceType)}
                                                            <h3 className="font-medium text-sm">{category.name}</h3>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1">
                                                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Trust level</label>
                                                            <Select
                                                                value={trustLevel}
                                                                onValueChange={(value) => handleTrustLevelChange(category.name, profileName, value, category)}
                                                            >
                                                                <SelectTrigger className="w-40 h-8 text-xs border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {trustLevelOptions
                                                                        .filter((opt) => trustLevel === "not_configured" || opt.value !== "not_configured")
                                                                        .map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                </Card>
                                            );
                                        })
                                )}
                            </div>
                            {totalCategoriesPages > 1 && (
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-muted-foreground">
                                        Page {currentCategoriesPage} of {totalCategoriesPages}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentCategoriesPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentCategoriesPage === 1}
                                            data-testid="button-categories-prev"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentCategoriesPage(prev => Math.min(totalCategoriesPages, prev + 1))}
                                            disabled={currentCategoriesPage === totalCategoriesPages}
                                            data-testid="button-categories-next"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Items */}
                        <div className="flex-1">
                            <Input
                                placeholder={`Search ${selectedResourceType} items...`}
                                value={itemsSearch}
                                onChange={e => setItemsSearch(e.target.value)}
                                className="pl-10 relative mb-4"
                            />
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />

                            <div className="space-y-2 mb-4">
                                {itemsLoading ? (
                                    Array(ITEMS_PER_PAGE).fill(0).map((_, idx) => <SkeletonCard key={idx} />)
                                ) : matchedResources.length > 0 ? (
                                    paginatedItems.map((item: any, idx: number) => {
                                        let displayValue = "";
                                        switch (selectedResourceType) {
                                            case "app": displayValue = item?.defaultProfileAppsResource?.appPath ?? ""; break;
                                            case "folder": displayValue = item.path ?? ""; break;
                                            case "url": displayValue = item.urlName ?? ""; break;
                                        }
                                        return (
                                            <Card key={idx} className="p-3 mb-2 hover-elevate transition-all duration-300">
                                                <div className="flex items-center gap-3">
                                                    <div className="mt-[2px] flex-shrink-0">
                                                        {getResourceTypeIcon(selectedResourceType)}
                                                    </div>
                                                    <div className="text-sm font-mono text-foreground break-all">{displayValue || "N/A"}</div>
                                                </div>
                                            </Card>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">No items found in this category</div>
                                )}
                            </div>
                            {selectedCategory && totalItemsPages > 1 && (
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {Math.min((currentItemsPage - 1) * ITEMS_PER_PAGE + 1, filteredItems.length)} to {Math.min(currentItemsPage * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} items
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentItemsPage === 1}
                                            data-testid="button-items-prev"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <span className="text-sm text-muted-foreground">
                                            Page {currentItemsPage} of {totalItemsPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentItemsPage(prev => Math.min(totalItemsPages, prev + 1))}
                                            disabled={currentItemsPage === totalItemsPages}
                                            data-testid="button-items-next"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
